import { useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/common/PageHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ShieldCheck } from "lucide-react";
import EbrInmobiliaria from "@/pages/inmobiliaria/EbrInmobiliaria";
import AvEbrCalibracion from "@/components/riesgo/av/AvEbrCalibracion";
import AvEbrControles from "@/components/riesgo/av/AvEbrControles";
import DocumentosLegales from "@/pages/DocumentosLegales";
import CanalDenuncia from "@/pages/CanalDenuncia";
import Capacitacion from "@/pages/Capacitacion";
import Auditoria from "@/pages/Auditoria";

/**
 * Módulo "Cumplimiento" — Fase PLD puro (iter 07-26).
 *
 * Aglutina en UNA sola entrada del sidebar los 5 submódulos de cumplimiento del vertical
 * inmobiliario. Cada uno vive en su TAB (regla PO: "una sola entrada por módulo; si algo
 * tiene partes, van en pestañas dentro de la pantalla, NO en el menú").
 *
 * Tabs:
 *  - EBR institucional inmobiliaria (matriz de riesgo del giro, LFPIORPI Art. 18).
 *  - Manual PLD (documentos legales del tenant, política interna).
 *  - Capacitación (registro anual de capacitación del personal — placeholder por ahora).
 *  - Canal de denuncia interno (empleado → OC).
 *  - Bitácora (auditoría de acciones críticas — placeholder de link al log).
 *
 * feb-2026 · iter 07-26: la pestaña activa se puede seleccionar vía `?tab=<clave>` (usado
 * por el CTA del banner EBR del dashboard cuando el giro es Inmobiliaria).
 * Reutiliza los componentes ya construidos sin tocar su lógica.
 */
const TABS_VALIDOS = new Set(["ebr", "manual", "capacitacion", "denuncia", "bitacora"]);
// sep-2026 — sub-pestañas del EBR seleccionables con `?tab=ebr&sub=calibracion`.
const SUBTABS_EBR = new Set(["panorama", "calibracion", "controles"]);

export default function CumplimientoInmo() {
  const [params] = useSearchParams();
  const tabInicial = TABS_VALIDOS.has(params.get("tab")) ? params.get("tab") : "ebr";
  const subTabInicial = SUBTABS_EBR.has(params.get("sub")) ? params.get("sub") : "panorama";
  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="inmo-cumplimiento-page">
      <PageHeader
        title="Cumplimiento"
        subtitle="EBR · Manual PLD · Capacitación · Canal de denuncia · Bitácora"
        icon={ShieldCheck}
      />
      <Tabs defaultValue={tabInicial} className="space-y-3">
        <TabsList className="grid w-full grid-cols-2 md:grid-cols-5">
          <TabsTrigger value="ebr" data-testid="tab-ebr">EBR</TabsTrigger>
          <TabsTrigger value="manual" data-testid="tab-manual">Manual PLD</TabsTrigger>
          <TabsTrigger value="capacitacion" data-testid="tab-capacitacion">Capacitación</TabsTrigger>
          <TabsTrigger value="denuncia" data-testid="tab-denuncia">Canal de denuncia</TabsTrigger>
          <TabsTrigger value="bitacora" data-testid="tab-bitacora">Bitácora</TabsTrigger>
        </TabsList>

        <TabsContent value="ebr">
          {/* sep-2026 · TEMA 3 — el EBR del vertical deja de ser solo lectura: el responsable
              de cumplimiento ya CALIBRA su metodología (ponderaciones, umbrales, escalas y
              catálogos) y registra mitigantes e indicadores 139 Quáter / 400 Bis, sin salir
              de esta pantalla. Sub-pestañas: no se agregan entradas al menú lateral. */}
          <Tabs defaultValue={subTabInicial} className="space-y-3">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="panorama" data-testid="tab-ebr-panorama">Panorama</TabsTrigger>
              <TabsTrigger value="calibracion" data-testid="tab-ebr-calibracion">Calibración</TabsTrigger>
              <TabsTrigger value="controles" data-testid="tab-ebr-controles">Mitigantes e indicadores</TabsTrigger>
            </TabsList>
            <TabsContent value="panorama">
              <EbrInmobiliaria />
            </TabsContent>
            <TabsContent value="calibracion">
              <div className="p-1 md:p-2">
                <AvEbrCalibracion />
              </div>
            </TabsContent>
            <TabsContent value="controles">
              <div className="p-1 md:p-2">
                <AvEbrControles />
              </div>
            </TabsContent>
          </Tabs>
        </TabsContent>
        <TabsContent value="manual">
          <DocumentosLegales />
        </TabsContent>
        <TabsContent value="capacitacion">
          {/* ago-2026 · deja de ser un placeholder: monta el módulo real de Capacitación
              PLD/FT (`/api/capacitacion/*`), que no tiene gate de giro y ya sirve a las 17
              actividades del Art. 17 (obligación del Art. 20 LFPIORPI). */}
          <Capacitacion />
        </TabsContent>
        <TabsContent value="denuncia">
          <CanalDenuncia />
        </TabsContent>
        <TabsContent value="bitacora">
          {/* ago-2026 · deja de ser un placeholder: monta la vista real de la bitácora
              (`GET /api/bitacora`), ya aislada por entidad. */}
          <Auditoria />
        </TabsContent>
      </Tabs>
    </div>
  );
}
